import { GridColumn } from '@progress/kendo-react-grid'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { useLogEventOnMount } from '~/hooks/useLogEventOnMount'

import { dialogInfoAtom } from '../atoms'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useMainQuery } from '../hooks/useMainQuery'

import { DeleteCell } from './DeleteCell'

const commandsColumnWidth = 12 + 96 + 12

export const AllowedDomainList = () => {
  const brandAccountId = useBrandAccountId()
  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const { data: domains } = useMainQuery()

  const { t } = useTranslation()

  useLogEventOnMount('Access to allowed domain list')

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])

  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('brandAccountSettings'),
      createBreadcrumb('brandAccountAllowedDomainList', {
        brandAccountId,
      }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'add' })
  }, [setDialogInfo])

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack spacing={2} verticalAppearance="stretch">
        <HStack alignSelf="end" spacing={2}>
          <StyledButton
            buttonStyle="success"
            fillMode="outline"
            icon="add"
            onClick={handleAddButtonClick}
          >
            {t('add_allowed_domain')}
          </StyledButton>
        </HStack>

        <StyledGrid data={domains}>
          <GridColumn field="value" title={t('allowed_domain')} />

          <GridColumn cell={DeleteCell} width={commandsColumnWidth} />
        </StyledGrid>
      </VStack>
    </PageContainer>
  )
}
